import { APIResponse, apiFetch } from "@/lib/apiFetch";
import { API_V2 } from "@/config/api";
import Stripe from "stripe";
import { Account } from "@/types/models/Account";
import { apiRequest, axiosInstance } from "@gogive/services";
import { isAxiosError } from "axios";

export const routes = {
  get: `${API_V2}/accounts/me`,
  websiteAnalysis: `${API_V2}/accounts/me/website-analysis`,
  brandExtractionRetry: `${API_V2}/accounts/me/brand-extraction/retry`,
  getStats: `${API_V2}/accounts/stats`,
  getStripeAccount: `${API_V2}/accounts/payment-processor`,
  getStripeConnectLink: `${API_V2}/accounts/payment-processor/connect`,
  getStripePaymentSetup: `${API_V2}/accounts/stripe/payment_setup`,
  getStripePaymentMethod: `${API_V2}/accounts/stripe/method`,
  deleteStripePaymentMethod: `${API_V2}/accounts/stripe/method`,
  refreshStripePaymentMethod: `${API_V2}/accounts/stripe/method/refresh`,
  create: `${API_V2}/accounts`,
  update: `${API_V2}/accounts/me`,
  check: `${API_V2}/accounts/check`,
  uploadLogo: `${API_V2}/accounts/me/logo`,
  uploadBanner: `${API_V2}/accounts/me/banner`,
  getMessages: (accountId: string) =>
    `${API_V2}/accounts/${accountId}/messages`,
  globalSearch: `${API_V2}/accounts/global-search`,
};

export interface AccountStats {
  raised: number;
  raisedOfflineIncluded: number;
  average: number;
  max: number;
  median: number;
  donors: number;
  donations: number;
  roundupPlans: number;
  plans: {
    _sum: { grossAmount: number };
    _count: number;
    interval: "weekly" | "monthly";
  }[];
}

export interface AccountStatsQuery {
  startDate?: Date;
  endDate?: Date;
  campaignId?: number;
  fundraisingCampaignId?: string;
}

export interface DonationMessage {
  id: number;
  comment: string;
  grossAmount: number;
  gogiveAmount: number;
  createdAt: string;
  donorId: number | null;
  donor: { id: number; firstName: string; lastName: string } | null;
  /** Present when the API returns a name without a donor id (legacy payloads). */
  donorFirstName?: string;
  donorLastName?: string;
}

export interface MessagesResponse {
  result: DonationMessage[];
  page: number;
  pages: number;
  total: number;
}

type DonationMessageRaw = {
  id?: number;
  comment?: string;
  grossAmount?: number;
  gross_amount?: number;
  gogiveAmount?: number;
  gogive_amount?: number;
  createdAt?: string;
  created_at?: string;
  donorId?: number | string | null;
  donor_id?: number | string | null;
  donor?: {
    id?: number | string;
    firstName?: string;
    first_name?: string;
    lastName?: string;
    last_name?: string;
  } | null;
};

/** Normalizes API payloads (camelCase or snake_case) for Love Wall / messages. */
export function normalizeDonationMessage(
  raw: DonationMessageRaw,
): DonationMessage {
  const donorRaw = raw.donor;
  const donorIdCandidate =
    raw.donorId ??
    raw.donor_id ??
    donorRaw?.id ??
    null;

  const donorIdNum =
    donorIdCandidate != null && donorIdCandidate !== ""
      ? Number(donorIdCandidate)
      : NaN;
  const donorId =
    Number.isFinite(donorIdNum) && donorIdNum > 0 ? donorIdNum : null;

  const firstName =
    donorRaw?.firstName ?? donorRaw?.first_name ?? "";
  const lastName =
    donorRaw?.lastName ?? donorRaw?.last_name ?? "";

  return {
    id: Number(raw.id),
    comment: String(raw.comment ?? ""),
    grossAmount: Number(raw.grossAmount ?? raw.gross_amount ?? 0),
    gogiveAmount: Number(raw.gogiveAmount ?? raw.gogive_amount ?? 0),
    createdAt: String(raw.createdAt ?? raw.created_at ?? ""),
    donorId,
    donor:
      donorId != null
        ? {
            id: donorId,
            firstName,
            lastName,
          }
        : null,
    ...(donorId == null && (firstName || lastName)
      ? { donorFirstName: firstName, donorLastName: lastName }
      : {}),
  };
}

export function normalizeMessagesResponse(
  data: MessagesResponse & { result?: DonationMessageRaw[] },
): MessagesResponse {
  return {
    ...data,
    result: (data.result ?? []).map(normalizeDonationMessage),
  };
}

export interface StripePaymentSetupResponse {
  secret: string;
}

export interface PaymentMethodResponse {
  method: Stripe.PaymentMethod | undefined;
}

export const deletePaymentMethod = () =>
  apiFetch<APIResponse<null>>({
    method: "DELETE",
    url: routes.deleteStripePaymentMethod,
  });

export const refreshDefaultPaymentMethod = () =>
  apiFetch<APIResponse<null>>({
    method: "POST",
    url: routes.refreshStripePaymentMethod,
  });

export const updateAccount = (input: Pick<Account, "defaultRoundupMode">) =>
  apiFetch<APIResponse<Account>>({
    method: "POST",
    url: routes.update,
    body: input,
  });

export interface CreateAccountRequest {
  website: string;
  captchaToken: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
    position: string;
  };
}

export const createAccount = (input: CreateAccountRequest) =>
  apiRequest<Account>(routes.create, {
    method: "POST",
    data: input,
  });

export const markOnboardingComplete = () =>
  apiRequest<Account>(`${API_V2}/accounts/me/onboarding`, {
    method: "POST",
  });

export class BrandExtractionRetryConflictError extends Error {
  constructor() {
    super("A brand extraction is already running");
    this.name = "BrandExtractionRetryConflictError";
  }
}

export const retryBrandExtraction = async (): Promise<{ jobId: string }> => {
  try {
    const response = await axiosInstance.post<{ jobId: string }>(
      routes.brandExtractionRetry
    );
    return response.data;
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 409) {
      throw new BrandExtractionRetryConflictError();
    }
    if (isAxiosError(err) && err.response) {
      const message =
        (err.response.data as { message?: string })?.message ??
        "Request failed";
      throw new Error(message);
    }
    throw err;
  }
};

export type StripePaymentProcessorStatus = {
  stripeAccountId: string;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  requirementsDue: string[];
};

export const syncStripePaymentProcessor = () =>
  apiRequest<StripePaymentProcessorStatus>(routes.getStripeAccount, {
    method: "GET",
  });

export interface CheckAccountRequest {
  name: string;
  email: string;
  abn: string;
}

export const checkAccount = (input: CheckAccountRequest) =>
  apiRequest<null>(routes.check, {
    method: "POST",
    data: input,
  });

export const uploadLogo = (data: FormData) =>
  apiRequest<void>(routes.uploadLogo, {
    method: "POST",
    data,
  });

export const uploadBanner = (data: FormData) =>
  apiRequest<void>(routes.uploadBanner, {
    method: "POST",
    data,
  });

export interface GlobalSearchResult {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
}

export interface GlobalSearchResponse {
  result: GlobalSearchResult[];
}

export const globalSearch = (keyword: string) =>
  apiRequest<GlobalSearchResponse>(routes.globalSearch, {
    method: "GET",
    params: { keyword },
  });
